<template>
  <div>
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <feedback-success
          v-if="showableSuccessFeedback"
          :message="successMessage"
        />
      </transition>
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div v-if="isLoadingFollowUser" class="loading-section">
      <loading />
    </div>
    <div v-else-if="!isEmptyResult" class="follows__main">
      <div v-if="!isApp" class="follows__page-headline">
        <PageHeadline
          :title="`フォローしている人（${followUserCount}）`"
          text-align="left"
        />
      </div>
      <div class="follows__follow-user-boxes">
        <FollowUserBox
          v-for="followUser in followUsers"
          :key="followUser.screenName"
          :user-id="followUser.id"
          :icon-url="followUser.iconImageUrl"
          :nickname="followUser.nickname"
          :screen-name="followUser.screenName"
          :birthday-displayable="followUser.birthdayDisplayable"
          :birthday-month="followUser.birthdayMonth"
          :birthday-date="followUser.birthdayDate"
          @click-following-button="onClickFollowingButton"
        />
      </div>
      <div class="follows__pagination">
        <Pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @clicked="changePage"
        />
      </div>
      <div v-if="!isApp" class="follows__to-giftee-top-area">
        <a href="/" class="follows__to-giftee-top-link text-link-to-top">
          gifteeトップへ戻る
        </a>
      </div>
    </div>
    <div v-else class="follows__main">
      <PageHeadline
        v-if="!isApp"
        :title="`フォローしている人（${followUserCount}）`"
        text-align="left"
      />
      <div class="follows__empty-result-message-area">
        <div class="follows__empty-result-message--one-line-in-pc">
          <p>他の方のプロフィール画面から</p>
          <p>お相手をフォローすると</p>
        </div>
        <p>ここに表示されます</p>
      </div>
      <div v-if="!isApp" class="follows__to-giftee-top-area">
        <a href="/" class="follows__to-giftee-top-link text-link-to-top">
          gifteeトップへ戻る
        </a>
      </div>
    </div>
    <TextModal
      v-if="showUnfollowModal"
      :title-text="`${unfollowNickname}さんのフォローを解除しますか？`"
      caption-text=""
      primary-button-text="フォローを解除する"
      cancel-button-text="キャンセル"
      :primary-button-disabled="unfollowModalButtonDisabled"
      @clicked-modal-cover="closeUnfollowModal"
      @clicked-primary="unfollow"
      @clicked-cancel="closeUnfollowModal"
    />
  </div>
</template>

<script>
import Loading from '../../atoms/loadings/Loading.vue'
import FollowUserBox from '../../organism/mypages/follows/FollowUserBox.vue'
import Pagination from '../../organism/paginations/Pagination.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import PageHeadline from '../../molecules/headlines/PageHeadline.vue'
import TextModal from '../..//molecules/modals/text-modal.vue'
import axios from 'axios'
import url from 'url'
import qs from 'qs'

const LIMIT_PER_PAGE = 20

export default {
  components: {
    Loading,
    FollowUserBox,
    Pagination,
    FeedbackError,
    FeedbackSuccess,
    PageHeadline,
    TextModal
  },
  props: {
    isApp: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      followUserCount: 0,
      followUsers: [],
      isLoadingFollowUser: false,
      showPageCounts: [],
      currentPage: 1,
      totalPages: 1,
      showableErrorFeedback: false,
      errorMessage: '',
      showableSuccessFeedback: false,
      successMessage: '',
      unfollowNickname: '',
      unfollowScreenName: '',
      unfollowUserId: '',
      showUnfollowModal: false,
      unfollowModalButtonDisabled: false
    }
  },
  computed: {
    isEmptyResult() {
      return this.followUsers.length === 0
    }
  },
  mounted() {
    this.initializeParams()
    this.getFollowUsers(this.currentPage)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function () {
      this.initializeParams()
      await this.getFollowUsers(this.currentPage)
    }.bind(this)
  },
  methods: {
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.currentPage = parsed.page ? Number(parsed.page) : 1
    },
    updateUrlFromParams() {
      history.pushState(
        '',
        '',
        this.currentPage <= 1
          ? '/mypage/follows'
          : `/mypage/follows?page=${this.currentPage}`
      )
    },
    async getFollowUsers(page) {
      this.isLoadingFollowUser = true

      try {
        const res = await axios.get(
          `/api/v1/users/@me/follows?page=${page}&limit=${LIMIT_PER_PAGE}`
        )

        const { current_page, total_pages, count } = res.data.meta

        if (res.data.data.length === 0) {
          // フォロー解除してページ内のユーザーがいなくなった場合や適当に存在しないページにアクセスした場合に最後のページのデータを取得し直す
          if (count !== 0) {
            const lastPage =
              count % LIMIT_PER_PAGE === 0
                ? Math.floor(count / LIMIT_PER_PAGE)
                : Math.floor(count / LIMIT_PER_PAGE) + 1
            this.getFollowUsers(lastPage)
            return
          } else {
            this.followUserCount = 0
            this.followUsers = []
            this.isLoadingFollowUser = false
            return
          }
        }

        this.currentPage = current_page
        this.totalPages = total_pages
        this.followUserCount = count
        this.followUsers = res.data.data
          .map(followUser => followUser.attributes)
          .map(followUser => {
            return {
              id: followUser.id,
              iconImageUrl: followUser.small_icon_image_url,
              nickname: followUser.nickname,
              screenName: followUser.screen_name,
              birthdayMonth: followUser.birthday_month,
              birthdayDate: followUser.birthday_date,
              birthdayDisplayable: followUser.birthday_displayable
            }
          })

        // フォロー解除してページ内のユーザーがいなくなった場合や適当に存在しないページにアクセスした場合に移動した最後のページにURLを変更する
        this.updateUrlFromParams()
        this.isLoadingFollowUser = false
      } catch (error) {
        this.isLoadingFollowUser = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async changePage(page) {
      this.scrollToTop()
      await this.getFollowUsers(page)
      this.updateUrlFromParams()
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    enterSuccessFeedback() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    onClickFollowingButton(nickname, screenName, userId) {
      this.unfollowNickname = nickname
      this.unfollowScreenName = screenName
      this.unfollowUserId = userId
      this.showUnfollowModal = true
    },
    closeUnfollowModal() {
      this.unfollowNickname = ''
      this.unfollowScreenName = ''
      this.showUnfollowModal = false
      this.unfollowModalButtonDisabled = false
    },
    async unfollow() {
      if (!this.isApp) {
        dataLayer.push({
          event: 'click_profile__unfollow',
          followee_user_id: this.unfollowUserId,
          via: 'follows'
        })
      } else {
        logEvent('click_profile__unfollow', {
          followee_user_id: this.unfollowUserId,
          via: 'follows'
        })
      }

      this.unfollowModalButtonDisabled = true
      try {
        const res = await axios.patch('/api/v1/users/@me/follows/unfollow', {
          screen_name: this.unfollowScreenName
        })
        if (res.data.status === 200) {
          this.closeUnfollowModal()
          this.showFeedback('フォローを解除しました')
          this.getFollowUsers(this.currentPage)
        }
      } catch (error) {
        this.closeUnfollowModal()
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    showFeedback(message) {
      this.showableSuccessFeedback = true
      this.successMessage = message
    }
  }
}
</script>
<style lang="scss" scoped>
.loading-section {
  margin-top: 16px;
}
.follows {
  &__page-headline {
    text-align: left;
  }

  &__follow-user-boxes {
    margin-top: 16px;
    max-width: $max-contents-width;

    @media screen and (min-width: $max-contents-width) {
      margin: 40px auto 0;
    }
  }

  &__pagination {
    margin: 0 auto;
    background-color: $white;
    max-width: $max-contents-width;
    border-top: solid 1px $gray-2;
  }

  &__to-giftee-top-area {
    margin: 16px 0;
  }

  &__to-giftee-top-link {
    padding: 16px;
    margin: auto;
    display: block;
  }

  &__empty-result-message-area {
    margin-top: 16px;
    height: 94px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media screen and (min-width: $max-contents-width) {
      margin-top: 112px;
    }

    p {
      @extend %hint;
    }
  }

  &__empty-result-message {
    &--one-line-in-pc {
      @media screen and (min-width: $max-contents-width) {
        display: flex;
        justify-content: center;
      }
    }
  }
}
</style>
